import React from 'react';
import Icon from '../../../components/AppIcon';

const OverallScoreCard = ({ score, grade, topPercent, verdict, evaluationMeta, integrityScore }) => {
  const getStatus = (s) => {
    if (s >= 85) return { label: 'Exceptional', color: 'text-emerald-400', bg: 'bg-emerald-500/20', stroke: '#10E58F' };
    if (s >= 75) return { label: 'Strong', color: 'text-emerald-400', bg: 'bg-emerald-500/20', stroke: '#10E58F' };
    if (s >= 65) return { label: 'Good', color: 'text-sky-400', bg: 'bg-sky-500/20', stroke: '#38BDF8' };
    if (s >= 55) return { label: 'Adequate', color: 'text-amber-400', bg: 'bg-amber-500/20', stroke: '#F59E0B' };
    if (s >= 40) return { label: 'Below Average', color: 'text-orange-400', bg: 'bg-orange-500/20', stroke: '#FB923C' };
    if (s >= 25) return { label: 'Needs Work', color: 'text-red-400', bg: 'bg-red-500/20', stroke: '#EF4444' };
    return { label: 'Poor', color: 'text-red-500', bg: 'bg-red-600/20', stroke: '#DC2626' };
  };

  const getVerdictStyle = (v) => {
    const verdictMap = {
      'Strong Hire': { color: 'text-emerald-400', bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', icon: 'CheckCircle2' },
      'Hire': { color: 'text-sky-400', bg: 'bg-sky-500/15', border: 'border-sky-500/30', icon: 'CheckCircle' },
      'Lean Hire': { color: 'text-amber-400', bg: 'bg-amber-500/15', border: 'border-amber-500/30', icon: 'HelpCircle' },
      'No Hire': { color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30', icon: 'XCircle' },
      'Strong No Hire': { color: 'text-red-400', bg: 'bg-red-500/15', border: 'border-red-500/30', icon: 'XOctagon' },
    };
    return verdictMap[v] || verdictMap['Lean Hire'];
  };

  const getConfidenceStyle = (c) => {
    if (c === 'high') return { label: 'High Confidence', color: 'text-emerald-400', icon: 'Shield' };
    if (c === 'medium') return { label: 'Medium Confidence', color: 'text-amber-400', icon: 'ShieldAlert' };
    return { label: 'Low Confidence', color: 'text-red-400', icon: 'ShieldOff' };
  };

  const status = getStatus(score);
  const circumference = 2 * Math.PI * 45;
  const offset = circumference - (score / 100) * circumference;
  const verdictStyle = verdict ? getVerdictStyle(verdict) : null;
  const confidence = evaluationMeta ? getConfidenceStyle(evaluationMeta.confidence) : null;

  return (
    <div className="flex items-center gap-6 bg-[#111216] backdrop-blur-xl p-5 md:p-6 rounded-[2.5rem] border border-[#1d1e26] shadow-2xl relative overflow-hidden group hover:scale-[1.02] transition-all duration-500">
      {/* Subtle internal glow */}
      <div className={`absolute -top-10 -right-10 w-32 h-32 ${status.bg.replace('20', '10')} rounded-full blur-3xl group-hover:${status.bg} transition-colors`} />
      
      <div className="relative w-28 h-28 shrink-0">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="44.5" stroke="rgba(255,255,255,0.05)" strokeWidth="11" fill="none" />
          <circle cx="50" cy="50" r="44.5" stroke={status.stroke} strokeWidth="11" fill="none"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            className="transition-all duration-1000 ease-out" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-white leading-none tabular-nums tracking-tight">{score}</span>
          <span className={`text-[10px] font-black ${status.color} tracking-[0.2em] mt-1.5`}>Score</span>
        </div>
      </div>

      <div className="space-y-2 min-w-0 flex-1">
        {/* Status label */}
        <div className="flex items-center gap-2">
          <span className="text-base md:text-lg font-black text-white tracking-wider">{status.label}</span>
          <div className={`w-5 h-5 rounded-full ${status.bg} flex items-center justify-center`}>
            <Icon name={score >= 75 ? "CheckCircle" : score >= 55 ? "Activity" : "AlertCircle"} size={12} className={status.color} />
          </div>
        </div>

        {/* Verdict badge */}
        {verdictStyle && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${verdictStyle.bg} border ${verdictStyle.border}`}>
            <Icon name={verdictStyle.icon} size={12} className={verdictStyle.color} />
            <span className={`text-[10px] font-black tracking-wider ${verdictStyle.color}`}>{verdict}</span>
          </div>
        )}

        {/* Confidence + Grade row */}
        <div className="flex items-center gap-3 flex-wrap">
          {grade && (
            <span className="text-[10px] font-black text-slate-400 tracking-widest">Grade: <span className="text-white">{grade}</span></span>
          )}
          {confidence && (
            <div className="flex items-center gap-1">
              <Icon name={confidence.icon} size={10} className={confidence.color} />
              <span className={`text-[9px] font-bold ${confidence.color}`}>{confidence.label}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OverallScoreCard;